import React from 'react';
// src/pages/reports/ReportsHub.tsx
// A single landing page for the reports area, grouping the individual reports by
// what question they answer. The individual report routes remain unchanged.

import { useNavigate } from 'react-router-dom';
import {
  HeartHandshakeIcon, ScaleIcon, UsersIcon, CalendarClockIcon, ArrowRightIcon,
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Card from '../../components/ui/Card';

interface ReportLink { name: string; path: string; blurb: string; }
interface ReportGroup { title: string; icon: React.ComponentType<{ className?: string }>; reports: ReportLink[]; }

const GROUPS: ReportGroup[] = [
  {
    title: 'Preferences',
    icon: HeartHandshakeIcon,
    reports: [
      { name: 'Preference Satisfaction', path: '/reports/preference-satisfaction', blurb: 'How well allocations match what staff asked to teach.' },
      { name: 'Preference Match', path: '/reports/preference-match', blurb: 'Per-teacher preference-to-allocation match detail.' },
    ],
  },
  {
    title: 'Workload & FTE',
    icon: ScaleIcon,
    reports: [
      { name: 'Workload Equity', path: '/reports/workload-equity', blurb: 'Who is over- or under-loaded across the staff.' },
      { name: 'Staff FTE', path: '/reports/staff-fte', blurb: 'FTE by staff member, learning area and semester.' },
      { name: 'FTE & Days Off', path: '/reports/fte-days-off', blurb: 'Requested load and days off for next year, with per-staff detail.' },
    ],
  },
  {
    title: 'Staffing',
    icon: UsersIcon,
    reports: [
      { name: 'Staff by Teaching', path: '/reports/staff-by-teaching', blurb: 'Who is teaching what, by learning area.' },
      { name: 'Teaching Intentions', path: '/reports/teaching-intentions', blurb: 'Response rate and return intentions for the collection period.' },
      { name: 'Recruitment Gaps', path: '/reports/recruitment-gaps', blurb: 'Where next year’s demand outstrips available staff.' },
    ],
  },
  {
    title: 'Daily Organiser',
    icon: CalendarClockIcon,
    reports: [
      { name: 'RUAH Term Comparison', path: '/reports/ruah-term-comparison', blurb: 'RUAH class minutes compared across terms.' },
      { name: 'Timetabled Subs', path: '/reports/timetabled-subs', blurb: 'Timetabled substitution minutes for payroll.' },
    ],
  },
];

const ReportsHub: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className="space-y-8">
      <PageHeader title="Reports" description="Answer a question about preferences, workload, staffing or the daily organiser." />

      {GROUPS.map((group) => {
        const GroupIcon = group.icon;
        return (
          <section key={group.title}>
            <h2 className="flex items-center gap-2 text-section text-ink-900 mb-4">
              <GroupIcon className="h-5 w-5 text-navy-600" />
              {group.title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {group.reports.map((r) => (
                <Card key={r.path} interactive className="p-5" onClick={() => navigate(r.path)}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-card-title text-ink-900">{r.name}</h3>
                    <ArrowRightIcon className="h-4 w-4 text-ink-400 flex-shrink-0 mt-1" />
                  </div>
                  <p className="mt-1.5 text-body-sm text-ink-500">{r.blurb}</p>
                </Card>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default ReportsHub;
